import React, { useEffect, useState, useMemo } from 'react';
import {
  Box, Typography, Paper, CircularProgress, Alert, Button, IconButton,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Chip, Popover, ListItemText
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import { getGrantedPermissions, getMyPermissions, grantAccess, revokeAccess } from '@services/access-control.service.ts';
import { UserPermission, GrantAccessPayload, AccessModule, AccessPermission } from '@/types/access-control.types';
import { toastService } from '@services/toast.service.ts';
import { GrantAccessModal } from '@components/settings/GrantAccessForm.tsx';
import { TabIndices } from '../../common/i-doc/i-doc.registry.ts';

interface PivotedPermissions {
  [granteeId: string]: {
    user: { id: number; firstName: string; lastName: string; };
    permissions: {
      [moduleCode in AccessModule]?: {
        id: number;
        permissions: AccessPermission[];
        expiresAt: string | null;
      };
    };
  };
}

export const AccessSettingsTab: React.FC = () => {
  const [grantedPermissions, setGrantedPermissions] = useState<UserPermission[]>([]);
  const [myPermissions, setMyPermissions] = useState<UserPermission[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isGrantModalOpen, setIsGrantModalOpen] = useState(false);
  const [editingUserId, setEditingUserId] = useState<number | null>(null);

  const [popoverAnchor, setPopoverAnchor] = useState<HTMLElement | null>(null);
  const [selectedPermission, setSelectedPermission] = useState<UserPermission | null>(null);

  const fetchData = async () => {
    try {
      setLoading(true);
      setError(null);
      const [granted, my] = await Promise.all([getGrantedPermissions(), getMyPermissions()]);
      setGrantedPermissions(granted);
      setMyPermissions(my);
    } catch (err) {
      setError('Не вдалося завантажити дані про доступи.');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleOpenCreateModal = () => {
    setEditingUserId(null);
    setIsGrantModalOpen(true);
  };

  const handleOpenEditModal = (userId: number) => {
    setEditingUserId(userId);
    setIsGrantModalOpen(true);
  };

  const handleSaveChanges = async ({ toGrant, toRevoke }: { toGrant: GrantAccessPayload[], toRevoke: number[] }) => {
    try {
      const grantPromises = toGrant.map(payload => grantAccess(payload));
      const revokePromises = toRevoke.map(id => revokeAccess(id));
      
      await Promise.all([...grantPromises, ...revokePromises]);

      toastService.success(`Доступи успішно оновлено!`);
      setIsGrantModalOpen(false);
      fetchData();
    } catch (err) {
      toastService.error('Не вдалося оновити один або кілька доступів.');
      console.error(err);
    }
  };

  const handleRevokeSingleAccess = async (permissionId: number) => {
    if (window.confirm('Ви впевнені, що хочете відкликати цей доступ?')) {
      try {
        await revokeAccess(permissionId);
        toastService.success('Доступ успішно відкликано!');
        handlePopoverClose();
        fetchData();
      } catch (err) {
        toastService.error('Не вдалося відкликати доступ.');
        console.error(err);
      }
    }
  };

  const handlePopoverOpen = (event: React.MouseEvent<HTMLElement>, permission: UserPermission) => {
    setPopoverAnchor(event.currentTarget);
    setSelectedPermission(permission);
  };

  const handlePopoverClose = () => {
    setPopoverAnchor(null);
    setSelectedPermission(null);
  };

  const pivotedData = useMemo((): PivotedPermissions => {
    return grantedPermissions.reduce((acc, p) => {
      const granteeId = String(p.granteeId);
      if (!acc[granteeId]) {
        acc[granteeId] = {
          user: p.grantee || { id: p.granteeId, firstName: 'ID:', lastName: String(p.granteeId) },
          permissions: {},
        };
      }
      acc[granteeId].permissions[p.moduleCode] = {
        id: p.id,
        permissions: p.permissions,
        expiresAt: p.expiresAt,
      };
      return acc;
    }, {} as PivotedPermissions);
  }, [grantedPermissions]);

  const grantees = Object.values(pivotedData);
  const allModules = Object.values(AccessModule);
  const openPopover = Boolean(popoverAnchor);

  if (loading) return <CircularProgress />;
  if (error) return <Alert severity="error">{error}</Alert>;

  return (
    <Paper sx={{ p: 3 }} data-i-doc={TabIndices.SETTINGS_ACCESS}>
      <Typography variant="h6" gutterBottom>Налаштування доступів</Typography>

      <Box sx={{ mt: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h5" gutterBottom>Доступи, які ви надали</Typography>
          <Button variant="contained" onClick={handleOpenCreateModal}>Надати доступ</Button>
        </Box>

        <GrantAccessModal
          open={isGrantModalOpen}
          onClose={() => setIsGrantModalOpen(false)}
          onSave={handleSaveChanges}
          editingUserId={editingUserId}
        />

        {grantees.length > 0 ? (
          <TableContainer component={Paper} sx={{ mt: 2 }}>
            <Table stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 'bold' }}>Користувач</TableCell>
                  {allModules.map(module => (
                    <TableCell key={module} align="center" sx={{ fontWeight: 'bold' }}>{module}</TableCell>
                  ))}
                  <TableCell align="right" sx={{ fontWeight: 'bold' }}>Дії</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {grantees.map(grantee => (
                  <TableRow key={grantee.user.id}>
                    <TableCell>{`${grantee.user.firstName} ${grantee.user.lastName}`}</TableCell>
                    {allModules.map(module => {
                      const perm = grantee.permissions[module];
                      const originalPermission = grantedPermissions.find(p => p.id === perm?.id);
                      return (
                        <TableCell key={module} align="center">
                          {perm && originalPermission ? (
                            <Box onClick={(e) => handlePopoverOpen(e, originalPermission)} sx={{ cursor: 'pointer' }}>
                              {perm.permissions.map(pType => (
                                <Chip key={pType} label={pType} size="small" sx={{ m: 0.5 }} color={pType === 'write' ? 'primary' : 'default'} />
                              ))}
                            </Box>
                          ) : (<Typography variant="caption" color="text.secondary">-</Typography>)}
                        </TableCell>
                      );
                    })}
                    <TableCell align="right">
                      <IconButton onClick={() => handleOpenEditModal(grantee.user.id)}>
                        <EditIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        ) : (<Typography sx={{ mt: 2 }}>Ви ще не надавали нікому доступів.</Typography>)}
      </Box>

      <Popover
        open={openPopover}
        anchorEl={popoverAnchor}
        onClose={handlePopoverClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        transformOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        {selectedPermission && (
          <Box sx={{ p: 2, maxWidth: 300 }}>
            <Typography variant="subtitle2" gutterBottom>Деталі доступу</Typography>
            <Typography variant="body2"><strong>Кому:</strong> {selectedPermission.grantee?.firstName} {selectedPermission.grantee?.lastName}</Typography>
            <Typography variant="body2"><strong>Модуль:</strong> {selectedPermission.moduleCode}</Typography>
            <Typography variant="body2"><strong>До:</strong> {selectedPermission.expiresAt ? new Date(selectedPermission.expiresAt).toLocaleDateString() : 'Безстроково'}</Typography>
            {selectedPermission.notes && (
              <Typography variant="body2" sx={{ mt: 1 }}>
                <strong>Нотатка:</strong> {selectedPermission.notes}
              </Typography>
            )}
            <Button
              variant="outlined"
              color="error"
              size="small"
              startIcon={<DeleteIcon />}
              onClick={() => handleRevokeSingleAccess(selectedPermission.id)}
              sx={{ mt: 2, width: '100%' }}
            >
              Відкликати доступ
            </Button>
          </Box>
        )}
      </Popover>

      <Box sx={{ mt: 4 }}>
        <Typography variant="h5" gutterBottom>Доступи, надані вам</Typography>
        {myPermissions.length > 0 ? (
          myPermissions.map((p) => (
            <Paper key={p.id} variant="outlined" sx={{ p: 1.5, mb: 1 }}>
               <ListItemText
                    primary={`Надано: ${p.granter?.firstName || 'ID:'} ${p.granter?.lastName || p.granterId}`}
                    secondary={`Модуль: ${p.moduleCode}, Права: ${p.permissions.join(', ')}, До: ${p.expiresAt ? new Date(p.expiresAt).toLocaleDateString() : 'Безстроково'}`}
                  />
            </Paper>
          ))
        ) : (<Typography sx={{ mt: 2 }}>Вам ще не надавали жодних доступів.</Typography>)}
      </Box>
    </Paper>
  );
};
